// Releases / Profiles catalog page.
//
// Lifts the per-major ProfileRelease table out of the old /releases scroll
// and adds catalog-wide BI (total profiles, file count, size, OS rollup
// across active releases) so an operator can answer "what's actually in our
// catalog?" without opening every row.
//
// Each row links to /releases/profiles/:id for the inspection view.

import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import { CheckCircle2, Plus, Trash2 } from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "wouter";
import { hasRole, useAuth } from "@/lib/auth-context";
import { useFleetStatus, useProfileReleases } from "@/lib/releases/hooks.ts";
import type { FleetSnapshot, ProfileReleaseRow } from "@/lib/releases/types.ts";
import { useProfileReleaseMutations } from "@/lib/releases/use-profile-release-mutations.ts";

const DeleteReleaseDialog = lazy(
	() => import("@/components/delete-release-dialog"),
);

export function ProfilesReleases() {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");

	const releasesQuery = useProfileReleases();
	const fleetQuery = useFleetStatus();

	const [deleteTarget, setDeleteTarget] = useState<ProfileReleaseRow | null>(
		null,
	);
	const { activate, remove } = useProfileReleaseMutations({
		onDeleted: () => setDeleteTarget(null),
	});

	const grouped = useMemo(() => {
		const map = new Map<number, ProfileReleaseRow[]>();
		for (const r of releasesQuery.data?.releases ?? []) {
			if (!map.has(r.browserMajor)) map.set(r.browserMajor, []);
			map.get(r.browserMajor)?.push(r);
		}
		return Array.from(map.entries())
			.map(([major, rows]) => ({
				major,
				rows: rows.slice().sort((a, b) => compareSemver(b.version, a.version)),
			}))
			.sort((a, b) => b.major - a.major);
	}, [releasesQuery.data]);

	const installedByMajor = useMemo(() => {
		const out = new Map<string, string[]>();
		const snapshots =
			fleetQuery.data?.hosts
				?.map((h) => h.snapshot)
				.filter((s): s is FleetSnapshot => s != null) ??
			fleetQuery.data?.snapshots ??
			[];
		for (const s of snapshots) {
			if (!s.installedProfiles) continue;
			for (const [major, versions] of Object.entries(s.installedProfiles)) {
				for (const v of versions) {
					const stripped = v.startsWith("v") ? v.slice(1) : v;
					const key = `${major}:${stripped}`;
					if (!out.has(key)) out.set(key, []);
					const arr = out.get(key);
					if (arr && !arr.includes(s.hostname)) arr.push(s.hostname);
				}
			}
		}
		return out;
	}, [fleetQuery.data]);

	function hostsForRelease(r: ProfileReleaseRow): string[] {
		const versionedSlug = `${r.version}-${r.slug}`;
		return (
			installedByMajor.get(`${r.browserMajor}:${versionedSlug}`) ??
			installedByMajor.get(`${r.browserMajor}:${r.version}`) ??
			[]
		);
	}

	const catalog = useMemo(() => {
		const rows = releasesQuery.data?.releases ?? [];
		const active = rows.filter((r) => r.isActive);
		const totals = rows.reduce(
			(acc, r) => {
				acc.size += r.sizeBytes;
				acc.files += r.fileCount;
				return acc;
			},
			{ size: 0, files: 0 },
		);
		const osTotals = active.reduce(
			(acc, r) => {
				acc.win += r.oses.win;
				acc.mac += r.oses.mac;
				acc.linux += r.oses.linux;
				acc.android += r.oses.android;
				return acc;
			},
			{ win: 0, mac: 0, linux: 0, android: 0 },
		);
		const osTotal =
			osTotals.win + osTotals.mac + osTotals.linux + osTotals.android;
		return {
			totalReleases: rows.length,
			totalActive: active.length,
			totalFiles: totals.files,
			totalSize: totals.size,
			osTotals,
			osTotal,
		};
	}, [releasesQuery.data]);

	const isPending = releasesQuery.isPending;

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between gap-3">
				<div>
					<div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
						<Link href="/releases" className="hover:underline">
							Releases
						</Link>
						<span>›</span>
						<span>Profiles</span>
					</div>
					<h1 className="text-2xl font-semibold tracking-tight">Profiles</h1>
					<p className="text-sm text-muted-foreground">
						Profile bundles published at{" "}
						<code className="font-mono">/profiles/v[X]/profiles.tar.gz</code>.
						Workers install the Active version per major when they boot, when
						they are deployed, and when you run Sync now on a worker.
					</p>
				</div>
				{canPublish && (
					<Button asChild>
						<Link href="/releases/new/profiles">
							<Plus className="mr-2 size-4" /> Publish profiles
						</Link>
					</Button>
				)}
			</div>

			<CatalogStrip catalog={catalog} loading={isPending} />

			{isPending ? <Skeleton className="h-48" /> : null}

			{!isPending && grouped.length === 0 ? (
				<Card>
					<CardContent className="p-6 text-sm text-muted-foreground">
						No profile releases yet. Upload a raw bundle and publish to get
						started.
					</CardContent>
				</Card>
			) : null}

			{grouped.map(({ major, rows }) => {
				const active = rows.find((r) => r.isActive);
				return (
					<Card key={major}>
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-base">
								Chromium v{major}
								{active ? (
									<Badge variant="success">
										active {active.version}-{active.slug}
									</Badge>
								) : (
									<Badge variant="warning">no active version</Badge>
								)}
							</CardTitle>
							<CardDescription>
								{rows.length} release{rows.length === 1 ? "" : "s"} in this
								major
							</CardDescription>
						</CardHeader>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Version</TableHead>
										<TableHead>Slug</TableHead>
										<TableHead className="hidden md:table-cell">
											Description
										</TableHead>
										<TableHead>Files</TableHead>
										<TableHead>Size</TableHead>
										<TableHead className="hidden lg:table-cell">
											Published
										</TableHead>
										<TableHead className="text-right">Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map((r) => (
										<TableRow key={r.id}>
											<TableCell className="font-mono text-xs">
												<Link
													href={`/releases/profiles/${r.id}`}
													className="hover:underline"
												>
													{r.version}
												</Link>
												{r.isActive ? (
													<CheckCircle2
														aria-label="active"
														className="ml-1 inline size-3.5 text-emerald-500"
													/>
												) : null}
											</TableCell>
											<TableCell className="font-mono text-xs">
												{r.slug}
											</TableCell>
											<TableCell className="hidden max-w-xs truncate text-xs text-muted-foreground md:table-cell">
												{r.description ?? "—"}
											</TableCell>
											<TableCell className="font-mono tabular-nums text-xs">
												{r.fileCount.toLocaleString()}
											</TableCell>
											<TableCell className="font-mono tabular-nums text-xs">
												{formatBytes(r.sizeBytes)}
											</TableCell>
											<TableCell className="hidden text-xs text-muted-foreground lg:table-cell">
												{formatRelative(r.publishedAt)}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex items-center justify-end gap-2">
													<Button asChild size="sm" variant="ghost">
														<Link href={`/releases/profiles/${r.id}`}>
															Inspect
														</Link>
													</Button>
													{canPublish ? (
														<>
															<Button
																size="sm"
																variant={r.isActive ? "secondary" : "outline"}
																disabled={r.isActive || activate.isPending}
																onClick={() => activate.mutate(r.id)}
															>
																{r.isActive ? "Active" : "Activate"}
															</Button>
															<Button
																size="sm"
																variant="ghost"
																aria-label="Delete release"
																disabled={remove.isPending}
																onClick={() => setDeleteTarget(r)}
															>
																<Trash2 className="size-3.5" />
															</Button>
														</>
													) : null}
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				);
			})}

			{deleteTarget ? (
				<Suspense fallback={null}>
					<DeleteReleaseDialog
						open
						onOpenChange={(o) => {
							if (!o) setDeleteTarget(null);
						}}
						release={deleteTarget}
						fleetHostsInstalled={hostsForRelease(deleteTarget)}
						confirming={remove.isPending}
						onConfirm={(force) => remove.mutate({ id: deleteTarget.id, force })}
					/>
				</Suspense>
			) : null}
		</div>
	);
}

function CatalogStrip({
	catalog,
	loading,
}: {
	catalog: {
		totalReleases: number;
		totalActive: number;
		totalFiles: number;
		totalSize: number;
		osTotals: { win: number; mac: number; linux: number; android: number };
		osTotal: number;
	};
	loading: boolean;
}) {
	const items: Array<{ label: string; value: string; hint?: string }> = [
		{
			label: "Releases",
			value: catalog.totalReleases.toLocaleString(),
			hint: `${catalog.totalActive} active`,
		},
		{
			label: "Files",
			value: catalog.totalFiles.toLocaleString(),
			hint: "Across all releases",
		},
		{
			label: "Size",
			value: formatBytes(catalog.totalSize),
			hint: "Total bytes on disk",
		},
	];
	const osList: Array<{ key: keyof typeof catalog.osTotals; label: string }> = [
		{ key: "win", label: "Windows" },
		{ key: "android", label: "Android" },
		{ key: "mac", label: "macOS" },
		{ key: "linux", label: "Linux" },
	];

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<div className="grid grid-cols-3 gap-3">
				{items.map((it) => (
					<Card key={it.label} size="sm">
						<CardHeader>
							<CardDescription>{it.label}</CardDescription>
							<CardTitle className="font-mono text-2xl tabular-nums">
								{loading ? <Skeleton className="h-7 w-16" /> : it.value}
							</CardTitle>
						</CardHeader>
						{it.hint ? (
							<CardContent>
								<p className="text-xs text-muted-foreground">{it.hint}</p>
							</CardContent>
						) : null}
					</Card>
				))}
			</div>
			<Card size="sm">
				<CardHeader>
					<CardTitle className="text-base">OS distribution (active)</CardTitle>
					<CardDescription>
						Aggregated across active profile releases per major.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{loading ? (
						<Skeleton className="h-16 w-full" />
					) : catalog.osTotal === 0 ? (
						<p className="text-xs text-muted-foreground">
							No active releases yet.
						</p>
					) : (
						<div className="flex flex-col gap-2">
							{osList.map(({ key, label }) => {
								const value = catalog.osTotals[key];
								const pct = catalog.osTotal
									? Math.round((value / catalog.osTotal) * 100)
									: 0;
								return (
									<div key={key} className="flex items-center gap-3 text-xs">
										<span className="w-16 text-muted-foreground">{label}</span>
										<div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
											<div
												className="h-full bg-primary"
												style={{ width: `${pct}%` }}
											/>
										</div>
										<span className="w-16 text-right font-mono tabular-nums">
											{value.toLocaleString()}
										</span>
										<span className="w-10 text-right font-mono tabular-nums text-muted-foreground">
											{pct}%
										</span>
									</div>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
